import React from "react";

import CheckIcon from "@uui/Check";

import LoadingButton from "@mui/lab/LoadingButton";
import { SvgIconProps, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import styled from "@mui/material/styles/styled";

import ROUTES from "@app/common/routes";

import { Link } from "@rui/Link";
import Stack, { Row } from "@rui/Stack";

export const Container = styled(Stack)({
  maxHeight: "100%",
  borderRadius: "4px",
  overflow: "hidden",
  flexShrink: 0,
});

export const PlayerContainer = styled(Stack)(({ theme }) => ({
  color: "#FFF",
  backgroundColor: theme.palette.shades.primary[800],
  padding: theme.spacing(1.5),
  [theme.breakpoints.up("xs")]: {
    padding: theme.spacing(2),
  },
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  "& .player-controls": {
    backgroundColor: "transparent !important",
    padding: 0,
    "& .scrubber__markers": {
      display: "none",
    },
    "& .player-controls__controls": {
      transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      [theme.breakpoints.down("sm")]: {
        paddingLeft: 0,
        paddingRight: 0,
        "& .player-controls__controls__left": {
          // smoosh the buttons a little closer together since we're making them larger,
          // to ensure there's room for all of them
          gap: "4px",
        },
        "& .MuiIconButton-root": {
          height: "30px",
          width: "30px",
          "& svg": {
            height: "28px",
            width: "28px",
          },
        },
        "& .current-time > p": {
          fontSize: "1rem",
        },
      },
    },
    "& .scrubber": {
      transition: "padding 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    },
  },
}));

export const DisclaimerContainer = styled(Row)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1, 1, 1),
  backgroundColor: theme.palette.transparent.white[12],
  borderRadius: "8px",
  color: theme.palette.shades.ghost[600],
  width: "100%",
}));

export const TranscriptContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: "#FFF",
  padding: theme.spacing(2),
  overflowY: "auto",
}));

export const BetaBadge = styled(Stack)(({ theme }) => ({
  background: theme.palette.gradients.read,
  borderRadius: "2px",
  padding: theme.spacing(0, 0.5),
  fontFamily: theme.typography.fontFamilySecondary,
  fontWeight: 500,
  fontSize: "0.75rem",
  whiteSpace: "nowrap",
}));

type PlayerButtonProps = IconButtonProps & {
  Icon: React.FunctionComponent<SvgIconProps>;
  tooltip: string;
  active?: boolean;
  copied?: boolean;
};

export const PlayerButton = styled<React.FC<PlayerButtonProps>>(
  ({ Icon, active: _a, copied, tooltip, ...props }) => (
    <Tooltip
      title={tooltip}
      arrow
      placement="top"
      enterDelay={copied ? 0 : 1000}
    >
      <IconButton {...props}>{copied ? <CheckIcon /> : <Icon />}</IconButton>
    </Tooltip>
  )
)(({ copied, active, theme }) => ({
  height: "32px",
  width: "32px",
  borderRadius: "8px",
  backgroundColor: copied
    ? theme.palette.shades.meadow[500]
    : active
      ? theme.palette.shades.primary[500]
      : theme.palette.transparent.white[2],
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  "& svg": {
    fontSize: "20px",
    color: active ? "#FFF" : theme.palette.transparent.white[50],
  },
  "&:hover": {
    backgroundColor: copied
      ? theme.palette.shades.meadow[500]
      : active
        ? theme.palette.shades.primary[500]
        : theme.palette.transparent.white[12],
    "& svg": {
      color: "#FFF",
    },
  },
}));

export const AcknowledgeButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.transparent.white[12],
  padding: "6px",
  color: theme.palette.shades.ghost[600],
  minWidth: "fit-content",
  "&:hover": {
    color: theme.palette.shades.ghost[600],
    backgroundColor: theme.palette.transparent.white[20],
  },
}));

export const PromptContainer = styled(Row)(({ theme }) => ({
  backgroundColor: theme.palette.shades.secondary[100],
  padding: theme.spacing(1, 2),
  color: theme.palette.shades.neutral[800],
}));

type FeedbackButtonProps = IconButtonProps & { loading: boolean };

export const FeedbackButton = styled((props: FeedbackButtonProps) => (
  <IconButton component={LoadingButton} {...props} />
))(({ loading, theme }) => ({
  height: "28px",
  width: "28px",
  padding: "6px",
  minWidth: "28px",
  borderRadius: "4px",
  "& .MuiSvgIcon-root": {
    opacity: loading ? 0.3 : 1,
    color: theme.palette.shades.neutral[500],
    fontSize: "16px",
  },
  "&:hover": {
    backgroundColor: theme.palette.transparent.white[50],
    "& .MuiSvgIcon-root": {
      color: theme.palette.shades.primary[500],
    },
  },
}));

export const AddReadButton = styled((props) => (
  <Link to={ROUTES.CALENDAR_UPCOMING}>
    <Button {...props} variant="contained">
      Add Read
    </Button>
  </Link>
))(({ theme }) => ({
  whiteSpace: "nowrap",
  color: "#FFF",
  backgroundColor: theme.palette.shades.secondary[400],
  boxShadow: "none",
  padding: "6px 8px",
  borderRadius: "4px",
  "&:hover": {
    backgroundColor: theme.palette.shades.secondary[500],
  },
}));

export const MinimizedPlayerButton = styled(Button)(({ theme }) => ({
  color: "#FFF",
  backgroundColor: theme.palette.shades.primary[800],
  padding: theme.spacing(1, 2),
  boxShadow: "none",
  width: "fit-content",
  height: "34px",
  marginTop: theme.spacing(1),
  "&:hover": {
    color: "#FFF",
    backgroundColor: theme.palette.shades.primary[700],
  },
}));
